import {
  ArchiveBoxIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ArrowUturnLeftIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  FolderIcon,
  PencilSquareIcon,
  PlusIcon,
  RocketLaunchIcon,
  SparklesIcon,
  Squares2X2Icon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button } from '../../components/Button';
import { Input } from '../../components/inputs/Input';
import { Option, Select } from '../../components/inputs/Select';
import { ListItem } from '../../components/ListItem';
import { EarthLoader } from '../../components/loaders/EarthLoader';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { PageContent } from '../../components/page/PageContent';
import { StatusPill } from '../../components/Pill';
import { PageHeader } from '../../components/page/PageHeader';
import { DotsPopover } from '../../components/Popover/DotsPopover';
import { Tooltip } from '../../components/Tooltip';
import { applyObjectLibrary, clearObjectLibrary } from '../../hooks/modules/objectLibrary/objectLibraryRegistration';
import { useObjectLibraryEvents } from '../../hooks/modules/objectLibrary/useObjectLibraryEvents';
import { definitionSpec } from '../../hooks/modules/objectLibrary/objectLibraryRegistration';
import { OBJECT_FILE_EXTENSION } from '../../modules/objectLibrary/objectFile';
import { useObjectFiles } from '../../modules/objectLibrary/useObjectFiles';
import { CategoriesModal, type EditableCategory } from '../../modules/objectLibrary/CategoriesModal';
import { libraryCategoryOptions, toConfigInput, type ObjectLibraryConfigInputValue } from '../../modules/objectLibrary/categories';
import { GlyphPreview } from '../../modules/objectLibrary/GlyphPreview';
import { NewObjectModal } from '../../modules/objectLibrary/NewObjectModal';
import { isSmartSpec } from '../../modules/objectLibrary/smart';
import { TranslatedLabel } from '../../modules/objectLibrary/TranslatedLabel';
import { useSketchObjectRegistryVersion } from '../../modules/objectLibrary/useSketchObjectRegistryVersion';
import { DIMENSION_CATEGORY_ID, OPENING_CATEGORY_IDS, type ObjectOpeningKind } from 'shared-types';
import { BUILTIN_OBJECT_SPECS } from '../../potree/sketchObjects/custom/builtins';
import { builtinTypeToSpec } from '../../potree/sketchObjects/custom/fromBuiltin';
import { BUILTIN_OPENING_ENTRIES, OPENING_CATEGORY_LABELS, builtinOpeningGlyph, builtinOpeningToSpec, type BuiltinOpeningEntry } from '../../potree/sketchObjects/custom/openings/builtinOpenings';
import { getCustomOpeningTypes } from '../../potree/sketchObjects/custom/openings/customOpeningRegistry';
import { evaluateCustomOpeningLocal } from '../../potree/sketchObjects/custom/openings/evaluateCustomOpening';
import { getSketchObjectLibrary, getSketchObjectLibraryConfig, getSketchObjectType, isSketchObjectTypeHidden, sketchObjectTypeCategory, type RegisteredSketchObjectType } from '../../potree/sketchObjects/registry';
import { defaultTypeGlyph } from '../../potree/sketchObjects/typeIcon';
import { T, useT, useTranslator } from '../../translation/src';
import {
  ObjectDefinitionStatus,
  useObjectDefinitionCreateMutation,
  useObjectDefinitionDeleteMutation,
  useObjectDefinitionPublishMutation,
  useObjectDefinitionSetArchivedMutation,
  useOrganisationObjectLibraryQuery,
  useOrganisationObjectLibraryUpdateConfigMutation,
  type ObjectDefinitionFieldsFragment,
} from '../../types/graphqlTypes';

type Filter = 'all' | 'builtin' | 'custom' | 'drafts' | 'hidden';

const FILTERS: ReadonlyArray<{ id: Filter; label: string }> = [
  { id: 'all', label: 'all' },
  { id: 'builtin', label: 'built-in' },
  { id: 'custom', label: 'custom' },
  { id: 'drafts', label: 'drafts' },
  { id: 'hidden', label: 'hidden' },
];

interface LibraryEntry {
  /** The type id the library config (hidden, order) refers to. */
  id: string;
  /** Plain text for organisation objects, a translation key for built-ins. */
  label: string;
  definition: ObjectDefinitionFieldsFragment | null;
  hidden: boolean;
  glyph: number[];
  /** Doors / windows: they stay in their fixed category. */
  opening: ObjectOpeningKind | null;
  /** Dimensions: they stay in their fixed category too (and are never smart). */
  dimension: boolean;
  /** The object's sketch-object type (built-in or organisation); null for doors / windows. */
  objectType: RegisteredSketchObjectType | null;
  /** A built-in door / window symbol: always offered, not reorderable. */
  builtinOpening: BuiltinOpeningEntry | null;
  /** A smart object: the organisation's own by its flag, a built-in when it has options to adjust. */
  smart: boolean;
}

/** Which cards a dragged card can land among: objects anywhere in their sections, doors / windows / dimensions only in their own. */
const dropGroupOf = (entry: Pick<LibraryEntry, 'opening' | 'dimension'>): string => (entry.opening ? OPENING_CATEGORY_IDS[entry.opening] : entry.dimension ? DIMENSION_CATEGORY_ID : 'objects');

/** The library's sections: Doors and Windows (fixed), then the object categories. */
interface LibrarySectionView {
  category: string;
  label: string;
  entries: LibraryEntry[];
}

const DRAG_MIME = 'application/x-pointorama-object-type';

type DropSide = 'before' | 'after';

/** Where a drop lands (a card and a side) and where to draw the line (viewport px). */
interface DropTarget {
  typeId: string;
  side: DropSide;
  left: number;
  top: number;
  height: number;
}

/** Half the card grid's gap (gap-3 = 12px): the line sits in the middle of the gap. */
const HALF_GAP = 6;

/**
 * Where a drop at (x, y) lands: first the card ROW nearest vertically (so a
 * pointer far to the side of a short row still targets that row, not the end
 * of a longer one above), then the nearest card in that row, and the side of
 * it the pointer is on. Anywhere on the page counts.
 */
function nearestDropTarget(root: HTMLElement | null, x: number, y: number, group: string): DropTarget | null {
  if (!root) return null;
  const cards = Array.from(root.querySelectorAll<HTMLElement>(`[data-drop-type][data-drop-group="${group}"]`), (el) => ({ el, rect: el.getBoundingClientRect() }));
  if (cards.length === 0) return null;
  const verticalGap = (rect: DOMRect): number => Math.max(rect.top - y, 0, y - rect.bottom);
  const rowGap = Math.min(...cards.map((c) => verticalGap(c.rect)));
  // The nearest row: every card as close vertically (cards of one row share their top).
  const rowTop = cards.find((c) => verticalGap(c.rect) === rowGap)?.rect.top ?? 0;
  const row = cards.filter((c) => Math.abs(c.rect.top - rowTop) < 2);
  const best = row.reduce((a, b) => (Math.max(a.rect.left - x, 0, x - a.rect.right) <= Math.max(b.rect.left - x, 0, x - b.rect.right) ? a : b));
  const typeId = best.el.dataset['dropType'];
  if (!typeId) return null;
  const side: DropSide = x < best.rect.left + best.rect.width / 2 ? 'before' : 'after';
  return { typeId, side, left: side === 'before' ? best.rect.left - HALF_GAP : best.rect.right + HALF_GAP, top: best.rect.top, height: best.rect.height };
}

// ─── Card ───────────────────────────────────────────────────────────────

interface LibraryCardProps {
  entry: LibraryEntry;
  canEdit: boolean;
  onToggleHidden: (entry: LibraryEntry) => void;
  onEdit: (entry: LibraryEntry) => void;
  onDuplicate: (entry: LibraryEntry) => void;
  /** A built-in card was clicked: it is copied to edit (after the user agrees). */
  onOpenBuiltin: (entry: LibraryEntry) => void;
  /** Clone it as a smart object (changed through the AI). */
  onMakeSmart: (entry: LibraryEntry) => void;
  onMove: (entry: LibraryEntry) => void;
  onRemove: (entry: LibraryEntry) => void;
  onPublish: (entry: LibraryEntry) => void;
  onExport: (entry: LibraryEntry) => void;
  /** This card is the one being dragged (shown faded). */
  dragging: boolean;
  onDragStart: (entry: LibraryEntry) => void;
  onDragEnd: () => void;
}

/** The app's status pills: grey = not live yet, yellow = needs attention, green = live. */
const PublishState: React.FC2<{ status: 'process' | 'warning' | 'pending' }> = ({ status, children }) => (
  <StatusPill status={status} className="text-[11px] font-medium leading-none">
    {children}
  </StatusPill>
);

const LibraryCard_: React.FC<LibraryCardProps> = ({ entry, canEdit, onToggleHidden, onEdit, onDuplicate, onOpenBuiltin, onMakeSmart, onMove, onRemove, onPublish, onExport, dragging, onDragStart, onDragEnd }) => {
  const hideLabel = useT('hide from the library', { swc: true });
  const showLabel = useT('show in the library', { swc: true });
  const smartLabel = useT('smart object: changed by describing what you want', { swc: true });
  const { definition } = entry;
  const isDraft = definition?.status === ObjectDefinitionStatus.Draft;
  const toggleLabel = entry.hidden ? showLabel : hideLabel;
  // Built-in door / window symbols are always offered, in their fixed order.
  const arrangeable = canEdit && !entry.builtinOpening;

  return (
    <div
      data-drop-type={arrangeable ? entry.id : undefined}
      data-drop-group={dropGroupOf(entry)}
      draggable={arrangeable}
      onDragStart={(e) => {
        // Firefox only starts a drag with some data set.
        e.dataTransfer.setData(DRAG_MIME, entry.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(entry);
      }}
      onDragEnd={onDragEnd}
      // One click opens it: the organisation's own object in the editor, a built-in as a new copy (opened right away).
      onClick={() => canEdit && (definition ? onEdit(entry) : onOpenBuiltin(entry))}
      className={classNames(
        'group relative flex flex-col rounded-xl border p-3 transition-[box-shadow,opacity] hover:shadow-md',
        // Built-in objects in a neutral grey; the organisation's own on the regular card.
        definition ? 'border-gray-200 bg-white dark:border-gray-700 dark:bg-eerie-black' : 'border-gray-200 bg-gray-100 dark:border-gray-600 dark:bg-[#2a2a2a]',
        arrangeable ? 'cursor-grab active:cursor-grabbing' : canEdit && 'cursor-pointer',
        dragging && 'opacity-40',
      )}
    >
      {/* The smart designer's sparkles mark a smart object (top-left; the hide toggle sits next to it). */}
      {entry.smart && (
        <Tooltip message={<span>{smartLabel}</span>} delay={0} wrapperClassName="absolute left-3 top-3 !cursor-default">
          <SparklesIcon aria-label={smartLabel} className="h-4 w-4" />
        </Tooltip>
      )}
      <div className={classNames('h-20 text-gray-800 dark:text-gray-100', entry.hidden && 'opacity-30')}>
        <GlyphPreview segments={entry.glyph} />
      </div>
      <div className={classNames('mt-2 truncate text-sm font-medium', entry.hidden && 'text-gray-400')} title={entry.label}>
        {definition ? entry.label : <TranslatedLabel label={entry.label} swc />}
      </div>
      <div className="mt-1 flex min-h-4 flex-wrap gap-1">
        {/* Only the publish state of the organisation's own objects. */}
        {isDraft && <PublishState status="process">{<T _str="draft" swc />}</PublishState>}
        {definition && !isDraft && definition.hasUnpublishedChanges && <PublishState status="warning">{<T _str="unpublished changes" swc />}</PublishState>}
        {definition && !isDraft && !definition.hasUnpublishedChanges && <PublishState status="pending">{<T _str="published" swc />}</PublishState>}
      </div>

      {canEdit && (
        // The card's own controls: their clicks (the menu's too, though it renders elsewhere) never open the card.
        <div className="contents" onClick={(e) => e.stopPropagation()}>
          {arrangeable && (
            <Tooltip message={<span>{toggleLabel}</span>} wrapperClassName={classNames('absolute top-2', entry.smart ? 'left-8' : 'left-2')}>
              <button
                type="button"
                aria-label={toggleLabel}
                onClick={() => onToggleHidden(entry)}
                className={classNames('cursor-pointer rounded p-1 transition-opacity hover:bg-gray-100 dark:hover:bg-white/10', entry.hidden ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100')}
              >
                {entry.hidden ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
              </button>
            </Tooltip>
          )}
          <DotsPopover anchor="bottom start" fitContent>
            <div className="flex flex-col">
              {definition && (isDraft || definition.hasUnpublishedChanges) && (
                <ListItem icon={RocketLaunchIcon} onClick={() => onPublish(entry)}>
                  {isDraft ? <T _str="publish" swc /> : <T _str="publish changes" swc />}
                </ListItem>
              )}
              {definition && (
                <ListItem icon={PencilSquareIcon} onClick={() => onEdit(entry)}>
                  <T _str="edit" swc />
                </ListItem>
              )}
              <ListItem icon={DocumentDuplicateIcon} onClick={() => onDuplicate(entry)}>
                {definition ? <T _str="duplicate" swc /> : <T _str="duplicate to edit" swc />}
              </ListItem>
              {!entry.smart && !entry.dimension && (
                <ListItem icon={SparklesIcon} onClick={() => onMakeSmart(entry)}>
                  <T _str="make a smart copy" swc />
                </ListItem>
              )}
              <ListItem icon={ArrowDownTrayIcon} onClick={() => onExport(entry)}>
                <T _str="export to file" swc />
              </ListItem>
              {!entry.opening && !entry.dimension && (
                <ListItem icon={FolderIcon} onClick={() => onMove(entry)}>
                  <T _str="move to category" swc />
                </ListItem>
              )}
              {arrangeable && (
                <ListItem icon={entry.hidden ? EyeIcon : EyeSlashIcon} onClick={() => onToggleHidden(entry)}>
                  {toggleLabel}
                </ListItem>
              )}
              {definition && (
                <ListItem icon={isDraft ? TrashIcon : ArchiveBoxIcon} onClick={() => onRemove(entry)} className="text-error-red-500">
                  {isDraft ? <T _str="delete" swc /> : <T _str="archive" swc />}
                </ListItem>
              )}
            </div>
          </DotsPopover>
        </div>
      )}
    </div>
  );
};
const LibraryCard = memo(LibraryCard_);

// ─── Move-to-category dialog ────────────────────────────────────────────

const MoveModal: React.FC<{ entry: LibraryEntry | null; categories: ReturnType<typeof libraryCategoryOptions>; onClose: () => void; onMove: (entry: LibraryEntry, categoryId: string) => Promise<void> }> = ({
  entry,
  categories,
  onClose,
  onMove,
}) => {
  // Seeded from the entry; the page remounts this dialog per entry (key), so no syncing effect.
  const [categoryId, setCategoryId] = useState(() => (entry?.objectType ? sketchObjectTypeCategory(entry.objectType) : ''));
  const [saving, setSaving] = useState(false);
  return (
    <Modal.Container open={!!entry} onClose={onClose} panelClassName="max-w-sm">
      <Modal.Header onClose={onClose}>
        <T _str="move to category" swc />
      </Modal.Header>
      <Select anchored value={categoryId} onChange={(e) => setCategoryId(e.target.value)} renderSelectedValue={(id) => <TranslatedLabel label={categories.find((c) => c.id === id)?.label ?? id} swc />}>
        {categories.map((c) => (
          <Option key={c.id} value={c.id}>
            <TranslatedLabel label={c.label} swc />
          </Option>
        ))}
      </Select>
      <Modal.Footer className="justify-end gap-2">
        <Button variant="secondary" size="xs" onClick={onClose}>
          <T _str="cancel" swc />
        </Button>
        <Button
          variant="primary"
          size="xs"
          loading={saving}
          onClick={async () => {
            if (!entry) return;
            setSaving(true);
            try {
              await onMove(entry, categoryId);
              onClose();
            } finally {
              setSaving(false);
            }
          }}
        >
          <T _str="move" swc />
        </Button>
      </Modal.Footer>
    </Modal.Container>
  );
};

// ─── Page ───────────────────────────────────────────────────────────────

/**
 * The organisation's object library: every type the design persona offers
 * (built-in and custom), grouped as the organisation arranged them. Editors
 * hide/show, reorder (drag), recategorise, duplicate and create objects;
 * everyone else sees it read-only.
 */
export const ObjectLibrary: React.FC2 = () => {
  const { organisationId = '' } = useParams();
  const navigate = useNavigate();
  const { data, loading, refetch } = useOrganisationObjectLibraryQuery({ variables: { organisationId, includeArchived: true }, fetchPolicy: 'cache-and-network' });
  const library = data?.organisationObjectLibrary;
  const canEdit = !!library?.canEdit;

  const [updateConfig] = useOrganisationObjectLibraryUpdateConfigMutation();
  const [createDefinition] = useObjectDefinitionCreateMutation();
  const [setArchived] = useObjectDefinitionSetArchivedMutation();
  const [deleteDefinition] = useObjectDefinitionDeleteMutation();
  const [publishDefinition] = useObjectDefinitionPublishMutation({ refetchQueries: ['objectDefinitionVersions'] });

  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  /** Bumped per open: a fresh category draft each time, without cutting the close animation. */
  const [categoriesSession, setCategoriesSession] = useState(0);
  const [moving, setMoving] = useState<LibraryEntry | null>(null);
  const [removing, setRemoving] = useState<LibraryEntry | null>(null);
  /** A built-in card clicked: it can't be edited itself, so a copy is offered. */
  const [copyingBuiltin, setCopyingBuiltin] = useState<LibraryEntry | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  /** The last failed action, shown inline until dismissed or the next action succeeds. */
  const [error, setError] = useState<string | null>(null);

  const translate = useTranslator();
  const t = {
    saveFailed: useT('the library could not be saved', { swc: true }),
    duplicateFailed: useT('the object could not be duplicated', { swc: true }),
    archived: useT('object archived: objects already placed in projects keep their look', { swc: true }),
    restored: useT('object restored', { swc: true }),
    published: useT('published: projects now use the new version', { swc: true }),
    publishFailed: useT('publishing failed: open the object to fix its problems', { swc: true }),
    deleted: useT('object deleted', { swc: true }),
    copySuffix: useT('copy'),
    search: useT('search objects', { swc: true }),
    dismiss: useT('dismiss', { swc: true }),
    exportFailed: useT('the object could not be exported', { swc: true }),
    importFailed: useT('these files could not be imported (not an object file, from a newer version, or damaged):', { swc: true }),
  };

  // Cards preview what editors see: drafts included.
  useEffect(() => {
    if (library) applyObjectLibrary(library, 'draft');
  }, [library]);
  useEffect(() => () => clearObjectLibrary(), []);
  useObjectLibraryEvents({ organisationId, onChanged: () => void refetch() });
  const registryVersion = useSketchObjectRegistryVersion();

  const definitionsByTypeId = useMemo(() => new Map((library?.definitions ?? []).map((d) => [d.typeId, d])), [library]);
  const categories = useMemo(() => libraryCategoryOptions(library?.config), [library?.config]);

  const sections = useMemo((): LibrarySectionView[] => {
    void registryVersion; // recompute when the registry (types, config) changes
    const query = search.trim().toLowerCase();
    const placementOrder = new Map(getSketchObjectLibraryConfig().typeOrder.map((p) => [p.typeId, p.order]));
    // Doors and Windows lead (fixed categories): the built-in symbols, then the organisation's in its order.
    const openingSections = (['DOOR', 'WINDOW'] as const).map(
      (kind): LibrarySectionView => ({
        category: OPENING_CATEGORY_IDS[kind],
        label: OPENING_CATEGORY_LABELS[kind],
        entries: [
          ...BUILTIN_OPENING_ENTRIES.filter((b) => b.kind === kind).map(
            (b): LibraryEntry => ({
              id: b.id,
              label: b.label,
              definition: null,
              hidden: false,
              glyph: builtinOpeningGlyph(b, kind === 'DOOR' ? 0.9 : 1.2),
              opening: kind,
              dimension: false,
              objectType: null,
              builtinOpening: b,
              smart: isSmartSpec(builtinOpeningToSpec(b)),
            }),
          ),
          ...getCustomOpeningTypes(kind)
            .filter((type) => type.listed)
            .map((type, index) => ({ type, index }))
            .sort((a, b) => (placementOrder.get(a.type.id) ?? Infinity) - (placementOrder.get(b.type.id) ?? Infinity) || a.index - b.index)
            .map(
              ({ type }): LibraryEntry => ({
                id: type.id,
                label: type.label,
                definition: definitionsByTypeId.get(type.id) ?? null,
                hidden: isSketchObjectTypeHidden(type.id),
                glyph: evaluateCustomOpeningLocal(type.spec, { width: type.spec.defaultSize.width }),
                opening: kind,
                dimension: false,
                objectType: null,
                builtinOpening: null,
                smart: definitionsByTypeId.get(type.id)?.smart ?? false,
              }),
            ),
        ],
      }),
    );
    // Then Dimensions (fixed category) and the object categories.
    const typeSections = [...getSketchObjectLibrary({ includeHidden: true, kind: 'dimensions' }), ...getSketchObjectLibrary({ includeHidden: true })].map(
      (section): LibrarySectionView => ({
        category: section.category,
        label: section.label,
        entries: section.types.map(
          (type): LibraryEntry => {
            const definition = definitionsByTypeId.get(type.id) ?? null;
            const twin = BUILTIN_OBJECT_SPECS[type.id];
            return {
              id: type.id,
              label: type.label,
              definition,
              hidden: isSketchObjectTypeHidden(type.id),
              glyph: defaultTypeGlyph(type),
              opening: null,
              dimension: type.dimension,
              objectType: type,
              builtinOpening: null,
              // Built-ins without a data twin are plain drawings: never smart.
              smart: definition ? definition.smart : !!twin && isSmartSpec(twin),
            };
          },
        ),
      }),
    );
    return [...openingSections, ...typeSections]
      .map((section) => ({
        ...section,
        entries: section.entries.filter((entry) => {
          if (filter === 'builtin' && entry.definition) return false;
          if (filter === 'custom' && !entry.definition) return false;
          if (filter === 'drafts' && !(entry.definition && (entry.definition.status === ObjectDefinitionStatus.Draft || entry.definition.hasUnpublishedChanges))) return false;
          if (filter === 'hidden' && !entry.hidden) return false;
          if (!query) return true;
          return [entry.label, ...(entry.definition?.tags ?? [])].join(' ').toLowerCase().includes(query);
        }),
      }))
      .filter((section) => section.entries.length > 0);
  }, [registryVersion, search, filter, definitionsByTypeId]);

  const archived = useMemo(() => (library?.definitions ?? []).filter((d) => d.status === ObjectDefinitionStatus.Archived), [library]);

  const saveConfig = useCallback(
    async (next: ObjectLibraryConfigInputValue) => {
      try {
        await updateConfig({ variables: { organisationId, config: next } });
        setError(null);
        await refetch();
      } catch {
        setError(t.saveFailed);
      }
    },
    [updateConfig, organisationId, refetch, t.saveFailed],
  );

  const currentConfig = useCallback((): ObjectLibraryConfigInputValue | null => (library ? toConfigInput(library.config) : null), [library]);

  const toggleHidden = useCallback(
    (entry: LibraryEntry) => {
      const config = currentConfig();
      if (!config) return;
      const hiddenTypeIds = entry.hidden ? config.hiddenTypeIds.filter((id) => id !== entry.id) : [...config.hiddenTypeIds, entry.id];
      void saveConfig({ ...config, hiddenTypeIds });
    },
    [currentConfig, saveConfig],
  );

  /**
   * Put the dragged type before/after `targetTypeId`, in the target's category
   * (so a drop into another section also moves it there). Positions come from
   * the full category, not the filtered view. Doors / windows are reordered
   * among their own kind only (the drop targets never offer anything else).
   */
  const dropAt = useCallback(
    (draggedTypeId: string, targetTypeId: string, side: DropSide) => {
      const config = currentConfig();
      if (!config || draggedTypeId === targetTypeId) return;
      const targetOpening = getCustomOpeningTypes().find((t) => t.id === targetTypeId);
      const target = getSketchObjectType(targetTypeId);
      let categoryId: string;
      let sectionIds: string[];
      if (targetOpening) {
        categoryId = OPENING_CATEGORY_IDS[targetOpening.kind];
        const placementOrder = new Map(config.typeOrder.map((p) => [p.typeId, p.order]));
        sectionIds = getCustomOpeningTypes(targetOpening.kind)
          .filter((t) => t.listed)
          .map((t, index) => ({ id: t.id, index }))
          .sort((a, b) => (placementOrder.get(a.id) ?? Infinity) - (placementOrder.get(b.id) ?? Infinity) || a.index - b.index)
          .map((t) => t.id);
      } else if (target) {
        categoryId = sketchObjectTypeCategory(target);
        const section = getSketchObjectLibrary({ includeHidden: true, kind: target.dimension ? 'dimensions' : 'objects' }).find((s) => s.category === categoryId);
        if (!section) return;
        sectionIds = section.types.map((t) => t.id);
      } else return;
      if (!sectionIds.includes(draggedTypeId) && targetOpening) return;
      const ordered = sectionIds.filter((id) => id !== draggedTypeId);
      ordered.splice(ordered.indexOf(targetTypeId) + (side === 'after' ? 1 : 0), 0, draggedTypeId);
      const typeOrder = [...config.typeOrder.filter((p) => !ordered.includes(p.typeId)), ...ordered.map((typeId, order) => ({ typeId, categoryId, order }))];
      void saveConfig({ ...config, typeOrder });
    },
    [currentConfig, saveConfig],
  );

  // ─── Drag & drop: the whole page is the drop zone; the nearest card decides where ──
  const [dragged, setDragged] = useState<{ id: string; group: string } | null>(null);
  const draggedId = dragged?.id ?? null;
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const onPageDragOver = (e: React.DragEvent) => {
    if (!draggedId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const next = nearestDropTarget(dropZoneRef.current, e.clientX, e.clientY, dragged?.group ?? '');
    setDropTarget((current) => (current && next && current.typeId === next.typeId && current.side === next.side && current.left === next.left && current.top === next.top ? current : next));
  };
  const endDrag = () => {
    setDragged(null);
    setDropTarget(null);
  };
  const onPageDrop = (e: React.DragEvent) => {
    if (!draggedId) return;
    e.preventDefault();
    if (dropTarget) dropAt(draggedId, dropTarget.typeId, dropTarget.side);
    endDrag();
  };

  const moveTo = useCallback(
    async (entry: LibraryEntry, categoryId: string) => {
      const config = currentConfig();
      if (!config) return;
      const inTarget = config.typeOrder.filter((p) => p.categoryId === categoryId && p.typeId !== entry.id);
      const order = inTarget.reduce((max, p) => Math.max(max, p.order + 1), 0);
      await saveConfig({ ...config, typeOrder: [...config.typeOrder.filter((p) => p.typeId !== entry.id), { typeId: entry.id, categoryId, order }] });
    },
    [currentConfig, saveConfig],
  );

  const saveCategories = useCallback(
    async (next: EditableCategory[]) => {
      const config = currentConfig();
      if (!config) return;
      const ids = new Set(next.map((c) => c.id));
      await saveConfig({
        ...config,
        categories: next.map((c, order) => ({ id: c.id, label: c.label, order })),
        // Types placed in a removed (empty) category fall back to their own.
        typeOrder: config.typeOrder.filter((p) => ids.has(p.categoryId) || categories.some((c) => c.builtin && c.id === p.categoryId)),
      });
    },
    [currentConfig, saveConfig, categories],
  );

  const openEditor = useCallback((definitionId: string) => navigate(`/organisations/${organisationId}/object-library/${definitionId}/edit`), [navigate, organisationId]);

  /** A copy being made: a second click meanwhile (a habitual double-click on a card) makes no second copy. */
  const copying = useRef(false);
  /** A copy to edit; `smart` makes it a smart object (a copy of a smart object always is one). */
  const duplicate = useCallback(
    async (entry: LibraryEntry, { smart: makeSmart = false }: { smart?: boolean } = {}) => {
      if (copying.current) return;
      copying.current = true;
      try {
        const { definition, objectType, builtinOpening } = entry;
        const smart = makeSmart || entry.smart;
        const result = definition
          ? await createDefinition({
              variables: {
                organisationId,
                name: `${definition.name} ${t.copySuffix}`,
                categoryId: definition.categoryId,
                spec: definition.draftSpec ?? definition.publishedSpec,
                duplicateOfDefinitionId: definition.id,
                smart,
              },
            })
          : builtinOpening
            ? await createDefinition({
                variables: {
                  organisationId,
                  name: translate(builtinOpening.label, { swc: true }),
                  categoryId: OPENING_CATEGORY_IDS[builtinOpening.kind],
                  spec: builtinOpeningToSpec(builtinOpening),
                  basedOnBuiltinId: builtinOpening.id,
                  smart,
                },
              })
            : objectType
              ? await createDefinition({ variables: { organisationId, name: objectType.label, categoryId: objectType.category, spec: builtinTypeToSpec(objectType), basedOnBuiltinId: objectType.id, smart } })
              : null;
        const id = result?.data?.objectDefinitionCreate.id;
        if (id) openEditor(id);
      } catch {
        setError(t.duplicateFailed);
      } finally {
        copying.current = false;
      }
    },
    [createDefinition, organisationId, openEditor, t.copySuffix, t.duplicateFailed, translate],
  );
  const makeSmartCopy = useCallback((entry: LibraryEntry) => void duplicate(entry, { smart: true }), [duplicate]);

  const remove = useCallback(async () => {
    const definition = removing?.definition;
    if (!definition) return;
    try {
      if (definition.status === ObjectDefinitionStatus.Draft) {
        await deleteDefinition({ variables: { organisationId, definitionId: definition.id } });
        toast.success(t.deleted);
      } else {
        await setArchived({ variables: { organisationId, definitionId: definition.id, archived: true } });
        toast.success(t.archived);
      }
      setError(null);
      await refetch();
    } catch {
      setError(t.saveFailed);
    }
  }, [removing, deleteDefinition, setArchived, organisationId, refetch, t.deleted, t.archived, t.saveFailed]);

  const restore = useCallback(
    async (definition: ObjectDefinitionFieldsFragment) => {
      try {
        await setArchived({ variables: { organisationId, definitionId: definition.id, archived: false } });
        toast.success(t.restored);
        setError(null);
        await refetch();
      } catch {
        setError(t.saveFailed);
      }
    },
    [setArchived, organisationId, refetch, t.restored, t.saveFailed],
  );

  const publish = useCallback(
    async (entry: LibraryEntry) => {
      if (!entry.definition) return;
      try {
        await publishDefinition({ variables: { organisationId, definitionId: entry.definition.id } });
        toast.success(t.published);
        setError(null);
        await refetch();
      } catch {
        setError(t.publishFailed);
      }
    },
    [publishDefinition, organisationId, refetch, t.published, t.publishFailed],
  );

  // ─── Export / import (.ptobj files) ────────────────────────────────
  const { exportObject, importFiles } = useObjectFiles(organisationId, categories);
  const importInput = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);

  const exportEntry = useCallback(
    async (entry: LibraryEntry) => {
      const categoryId = entry.builtinOpening ? OPENING_CATEGORY_IDS[entry.builtinOpening.kind] : (entry.definition?.categoryId ?? (entry.objectType ? sketchObjectTypeCategory(entry.objectType) : ''));
      // The drawing editors see: a definition's draft, a built-in's editable twin.
      const spec = entry.definition ? definitionSpec(entry.definition, 'draft') : entry.builtinOpening ? builtinOpeningToSpec(entry.builtinOpening) : entry.objectType ? builtinTypeToSpec(entry.objectType) : null;
      if (!spec) return;
      try {
        await exportObject({
          name: entry.definition ? entry.label : translate(entry.label, { swc: true }),
          categoryId,
          categoryLabel: categories.find((c) => c.id === categoryId)?.label ?? null,
          spec,
          definition: entry.definition,
        });
      } catch {
        setError(t.exportFailed);
      }
    },
    [exportObject, translate, categories, t.exportFailed],
  );

  const importFromFiles = useCallback(
    async (files: File[]) => {
      setImporting(true);
      try {
        const result = await importFiles(files);
        if (result.imported.length > 0) {
          toast.success(translate('imported {count} as drafts: open them to review and publish', { swc: true }).replace('{count}', String(result.imported.length)));
          await refetch();
        }
        setError(result.failed.length > 0 ? `${t.importFailed} ${result.failed.map((f) => f.file).join(', ')}` : null);
      } finally {
        setImporting(false);
      }
    },
    [importFiles, refetch, translate, t.importFailed],
  );

  const isCategoryInUse = useCallback((id: string) => !!library?.definitions.some((d) => d.categoryId === id), [library]);
  const removingIsDraft = removing?.definition?.status === ObjectDefinitionStatus.Draft;

  return (
    <PageContent>
      {/* The whole page takes the drop; the line shows where it lands. */}
      <div className="flex h-full min-h-0 flex-col" onDragOver={onPageDragOver} onDrop={onPageDrop}>
        <PageHeader
          title={<T _str="object library" swc />}
          button={
            canEdit ? (
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={() => {
                    setCategoriesSession((n) => n + 1);
                    setCategoriesOpen(true);
                  }}
                >
                  <Squares2X2Icon className="mr-1 w-4" />
                  <T _str="categories" swc />
                </Button>
                <Button variant="secondary" size="xs" onClick={() => importInput.current?.click()} loading={importing}>
                  <ArrowUpTrayIcon className="mr-1 w-4" />
                  <T _str="import" swc />
                </Button>
                <input
                  ref={importInput}
                  type="file"
                  multiple
                  accept={`${OBJECT_FILE_EXTENSION},application/json`}
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files ?? []);
                    e.target.value = '';
                    if (files.length > 0) void importFromFiles(files);
                  }}
                />
                <Button variant="primary" size="xs" onClick={() => setNewOpen(true)}>
                  <PlusIcon className="mr-1 w-4" />
                  <T _str="new object" swc />
                </Button>
              </div>
            ) : null
          }
        />

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t.search} className="h-9 w-64" />
          <div className="flex flex-wrap gap-1" role="tablist">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={filter === f.id}
                onClick={() => setFilter(f.id)}
                className={classNames(
                  'rounded-full border px-3 py-1 text-sm transition-colors',
                  filter === f.id ? 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black' : 'border-gray-300 hover:border-gray-500 dark:border-gray-600',
                )}
              >
                <TranslatedLabel label={f.label} swc />
              </button>
            ))}
          </div>
          {canEdit && (
            <p className="ml-auto text-xs text-gray-500 dark:text-gray-400">
              <T _str="drag cards to reorder; hidden objects are not offered in projects" swc />
            </p>
          )}
        </div>

        {error && (
          <div role="alert" className="mb-4 flex items-center gap-3 rounded-lg border border-error-red-500/40 bg-error-red-500/10 px-4 py-2.5 text-sm">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-error-red-500" />
            <span className="flex-1">{error}</span>
            <button type="button" aria-label={t.dismiss} title={t.dismiss} onClick={() => setError(null)} className="rounded p-0.5 hover:bg-black/5 dark:hover:bg-white/10">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}

        <div ref={dropZoneRef} className="w-full min-h-0 flex-1 overflow-y-auto pb-10">
          {loading && !library ? (
            <EarthLoader />
          ) : sections.length === 0 ? (
            <div className="py-16 text-center text-gray-500 dark:text-gray-400">
              <T _str="no objects match" swc />
            </div>
          ) : (
            <div className="flex flex-col gap-8">
              {sections.map((section) => (
                <section key={section.category}>
                  <h2 className="mb-3 flex items-baseline gap-2 text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                    <TranslatedLabel label={section.label} />
                    <span className="font-normal text-gray-400">{section.entries.length}</span>
                  </h2>
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3">
                    {section.entries.map((entry) => (
                      <LibraryCard
                        key={entry.id}
                        entry={entry}
                        canEdit={canEdit}
                        onToggleHidden={toggleHidden}
                        onEdit={(e) => e.definition && openEditor(e.definition.id)}
                        onDuplicate={duplicate}
                        onOpenBuiltin={setCopyingBuiltin}
                        onMakeSmart={makeSmartCopy}
                        onMove={setMoving}
                        onRemove={setRemoving}
                        onPublish={(e) => void publish(e)}
                        onExport={(e) => void exportEntry(e)}
                        dragging={draggedId === entry.id}
                        onDragStart={(e) => setDragged({ id: e.id, group: dropGroupOf(e) })}
                        onDragEnd={endDrag}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}

          {archived.length > 0 && (
            <section className="mt-10 border-t border-gray-200 pt-4 dark:border-gray-700">
              <button type="button" onClick={() => setShowArchived((v) => !v)} className="text-sm text-gray-600 hover:underline dark:text-gray-300">
                {showArchived ? <T _str="hide archived objects" swc /> : <T _str="show archived objects" swc />} ({archived.length})
              </button>
              {showArchived && (
                <ul className="mt-3 flex flex-col divide-y divide-gray-200 dark:divide-gray-700">
                  {archived.map((d) => (
                    <li key={d.id} className="flex items-center justify-between py-2 text-sm">
                      <span>{d.name}</span>
                      {canEdit && (
                        <button type="button" onClick={() => restore(d)} className="flex items-center gap-1 text-french-violet-600 hover:underline dark:text-neon-green-300">
                          <ArrowUturnLeftIcon className="h-4 w-4" />
                          <T _str="restore" swc />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </div>
      </div>
      {dropTarget && draggedId && (
        <div
          aria-hidden
          className="pointer-events-none fixed z-40 w-1 -translate-x-1/2 rounded-full bg-french-violet-600 shadow-[0_0_0_3px_rgba(124,58,237,0.25)] dark:bg-neon-green-300 dark:shadow-[0_0_0_3px_rgba(181,255,161,0.25)]"
          style={{ left: dropTarget.left, top: dropTarget.top, height: dropTarget.height }}
        />
      )}
      <NewObjectModal open={newOpen} onClose={() => setNewOpen(false)} organisationId={organisationId} config={library?.config} onCreated={openEditor} />
      <CategoriesModal
        key={categoriesSession}
        open={categoriesOpen}
        onClose={() => setCategoriesOpen(false)}
        categories={library?.config.categories.map(({ id, label }) => ({ id, label })) ?? []}
        isInUse={isCategoryInUse}
        onSave={saveCategories}
      />
      <MoveModal key={moving?.id ?? 'none'} entry={moving} categories={categories} onClose={() => setMoving(null)} onMove={moveTo} />
      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={remove}
        variant={removingIsDraft ? 'danger' : 'warning'}
        title={removingIsDraft ? <T _str="delete this draft?" swc /> : <T _str="archive this object?" swc />}
        message={removingIsDraft ? <T _str="the draft was never published, so no project uses it" swc /> : <T _str="it leaves the library; objects already placed in projects keep their look and it can be restored later" swc />}
        confirmLabel={removingIsDraft ? <T _str="delete" swc /> : <T _str="archive" swc />}
      />
      <ConfirmDialog
        open={!!copyingBuiltin}
        onClose={() => setCopyingBuiltin(null)}
        onConfirm={async () => {
          if (copyingBuiltin) await duplicate(copyingBuiltin);
        }}
        variant="info"
        title={<T _str="this is a built-in object" swc />}
        message={<T _str="built-in objects can't be changed. A copy is added to your library and opens in the editor." swc />}
        confirmLabel={<T _str="copy and edit" swc />}
      />
    </PageContent>
  );
};
